import { Outlet } from "react-router-dom";
import { PanelLeft } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { useUiStore } from "@/stores/ui-store";
import { LexeIcon } from "@/components/ui/LexeLogo";

function SidebarToggleFloat() {
  const setSidebarState = useUiStore.getState().setSidebarState;

  return (
    <button
      onClick={() => setSidebarState("expanded")}
      className="fixed left-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-8 h-8 rounded-r-lg border border-l-0 border-border bg-card shadow-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      title="Mostra sidebar"
    >
      <PanelLeft className="h-4 w-4" />
    </button>
  );
}

/**
 * Root layout — blocked until /admin/me resolves.
 * Shows skeleton during loading to prevent RBAC flicker.
 */
export function RootLayout() {
  const { isLoading, error, isAdmin, isSuperadmin } = useAdminAccess();
  const sidebarState = useUiStore((s) => s.sidebarState);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex h-screen bg-background">
        {/* Sidebar skeleton */}
        <aside className="w-60 border-r border-border bg-muted/30 animate-pulse">
          <div className="flex items-center gap-2 border-b border-border px-4 py-4">
            <LexeIcon size={32} />
            <div className="h-5 w-16 bg-muted rounded" />
          </div>
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-8 bg-muted rounded" />
            ))}
          </div>
        </aside>
        {/* Main skeleton */}
        <div className="flex-1">
          <div className="h-14 border-b border-border" />
          <div className="p-6">
            <div className="h-8 w-48 bg-muted rounded mb-6" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state — forbidden for non-admin users
  if (error || (!isAdmin && !isSuperadmin)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <LexeIcon size={48} className="mx-auto" />
          <h2 className="text-xl font-semibold text-foreground">Accesso negato</h2>
          <p className="text-muted-foreground max-w-md">
            {error?.details || "Non hai i permessi per accedere al pannello admin."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden relative transition-all duration-200">
        {sidebarState === "hidden" && <SidebarToggleFloat />}
        <TopBar />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
